// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {DataLoader, Styled} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {useModal, useState} from '@supermove/hooks';
import {
  CommunicationKindModel,
  ProductUser,
  ProjectModel,
  TextMessageTemplateModel,
  UserModel,
} from '@supermove/models';
import {colors} from '@supermove/styles';

// App
import Line from '@shared/design/components/Line';
import Tabs from '@shared/design/components/Tabs';
import PageLoadingIndicator from '@shared/modules/App/components/PageLoadingIndicator';
import TextMessageTemplateCategory from '@shared/modules/Sms/enums/TextMessageTemplateCategory';
import CommunicationInboxFooterSendCall from 'modules/Communication/CommunicationInbox/ProjectConversation/components/footer/CommunicationInboxFooterSendCall';
import CommunicationInboxFooterSendEmail from 'modules/Communication/CommunicationInbox/ProjectConversation/components/footer/CommunicationInboxFooterSendEmail';
import CommunicationInboxFooterSendText from 'modules/Communication/CommunicationInbox/ProjectConversation/components/footer/CommunicationInboxFooterSendText';
import DiscardEmailModal from 'modules/Communication/CommunicationInbox/ProjectConversation/components/footer/DiscardEmailModal';

const FooterContainer = Styled.View<{footerExpanded?: boolean}>`
  background-color: ${colors.white};
  ${({footerExpanded}) => (footerExpanded ? 'flex: 1;' : undefined)};
`;

const SkeletonContainer = Styled.View`
  height: 156px;
  align-items: center;
  justify-content: center;
`;

const ActionsContainer = Styled.View`
  flex-direction: row;
`;

const tabs: Array<{label: string; key: CommunicationKindModel}> = [
  {label: 'SMS', key: 'SMS'},
  {label: 'Email', key: 'EMAIL'},
  {label: 'Call', key: 'CALL'},
];

const SendCommunication = ({
  customer,
  viewer,
  refetch,
  project,
  textMessageTemplates,
  selectedTextMessageTemplate,
  replyThreadUuid,
  setReplyThreadUuid,
  activeActionType,
  footerExpanded,
  setFooterExpanded,
  setHasDraft,
}: {
  customer: UserModel;
  viewer: UserModel;
  refetch: () => void;
  project: ProjectModel;
  textMessageTemplates: TextMessageTemplateModel[];
  selectedTextMessageTemplate: TextMessageTemplateModel | undefined;
  replyThreadUuid?: string;
  setReplyThreadUuid: (replyThreadUuid?: string) => void;
  activeActionType: CommunicationKindModel;
  footerExpanded: boolean;
  setFooterExpanded: (footerExpanded: boolean) => void;
  setHasDraft: (hasDraft: boolean) => void;
}) => {
  if (activeActionType === 'EMAIL') {
    return (
      <CommunicationInboxFooterSendEmail
        customer={customer}
        refetch={refetch}
        project={project}
        replyThreadUuid={replyThreadUuid}
        setReplyThreadUuid={setReplyThreadUuid}
        footerExpanded={footerExpanded}
        setFooterExpanded={setFooterExpanded}
        setHasDraft={setHasDraft}
      />
    );
  }
  if (activeActionType === 'SMS') {
    return (
      <CommunicationInboxFooterSendText
        customer={customer}
        viewer={viewer}
        refetch={refetch}
        project={project}
        textMessageTemplates={textMessageTemplates}
        selectedTextMessageTemplate={selectedTextMessageTemplate}
      />
    );
  }
  return (
    <CommunicationInboxFooterSendCall
      customer={customer}
      viewer={viewer}
      refetch={refetch}
      project={project}
    />
  );
};

const Skeleton = ({showLoadingIndicator = true}: {showLoadingIndicator?: boolean}) => {
  return (
    <SkeletonContainer>{showLoadingIndicator ? <PageLoadingIndicator /> : null}</SkeletonContainer>
  );
};

const CommunicationInboxProjectConversationFooter = ({
  projectUuid,
  customerUuid,
  refetch: refetchParent,
  showSingleActionType,
  activeActionType,
  updateActiveActionType,
  replyThreadUuid,
  setReplyThreadUuid,
  footerExpanded,
  setFooterExpanded,
  selectedTextMessageTemplateUuid,
}: {
  refetch: () => void;
  projectUuid: string;
  customerUuid: string;
  showSingleActionType?: CommunicationKindModel;
  activeActionType: CommunicationKindModel;
  updateActiveActionType: (activeActionType: CommunicationKindModel) => void;
  replyThreadUuid?: string;
  setReplyThreadUuid: (replyThreadUuid?: string) => void;
  footerExpanded: boolean;
  setFooterExpanded: (footerExpanded: boolean) => void;
  selectedTextMessageTemplateUuid?: string;
}) => {
  const {loading, data, refetch} = useQuery<{
    viewer: UserModel;
    user: UserModel;
    project: ProjectModel;
  }>(CommunicationInboxProjectConversationFooter.query, {
    fetchPolicy: 'network-only',
    variables: {
      customerUuid,
      projectUuid,
      categories: [TextMessageTemplateCategory.CUSTOMER],
    },
    skip: !customerUuid || !projectUuid,
  });
  const [tabToNavigateTo, setTabToNavigateTo] = useState<CommunicationKindModel>();
  const [hasDraft, setHasDraft] = useState(false);
  const discardEmailModal = useModal({name: 'TabDiscardEmailModal'});

  if (!customerUuid || !projectUuid) {
    return <Skeleton showLoadingIndicator={false} />;
  }

  const tabsToShow = showSingleActionType
    ? tabs.filter((tab) => tab.key === showSingleActionType)
    : tabs;

  return (
    <FooterContainer footerExpanded={footerExpanded}>
      {footerExpanded ? null : <Line />}
      <ActionsContainer>
        <Tabs
          tabs={tabsToShow}
          handlePressTab={(tab) => {
            if (hasDraft) {
              setTabToNavigateTo(tab.key);
              discardEmailModal.handleOpen();
            } else {
              updateActiveActionType(tab.key);
            }
          }}
          activeTabIndex={tabsToShow.findIndex((tab) => tab.key === activeActionType)}
          TabComponent={Tabs.SecondaryTab}
          isEnclosed
          scrollViewStyle={{borderWidth: 0}}
          tabStyle={{borderRadius: 2}}
        />
      </ActionsContainer>
      <Line />
      <DataLoader loading={loading} data={data} as={Skeleton}>
        {({loadedData}) => {
          const selectedTextMessageTemplate = _.find(
            loadedData.project.organization.textMessageTemplatesByCategory,
            (template) => template.uuid === selectedTextMessageTemplateUuid,
          );
          return (
            <SendCommunication
              customer={loadedData.user}
              viewer={loadedData.viewer}
              refetch={() => {
                refetch();
                refetchParent();
              }}
              project={loadedData.project}
              textMessageTemplates={loadedData.project.organization.textMessageTemplatesByCategory}
              selectedTextMessageTemplate={selectedTextMessageTemplate}
              replyThreadUuid={replyThreadUuid}
              setReplyThreadUuid={setReplyThreadUuid}
              activeActionType={activeActionType}
              footerExpanded={footerExpanded}
              setFooterExpanded={setFooterExpanded}
              setHasDraft={setHasDraft}
            />
          );
        }}
      </DataLoader>
      <DiscardEmailModal
        isOpen={discardEmailModal.isOpen}
        handleClose={discardEmailModal.handleClose}
        handleDiscardEmail={() => {
          discardEmailModal.handleClose();
          if (tabToNavigateTo) {
            updateActiveActionType(tabToNavigateTo);
          }
          setTabToNavigateTo(undefined);
          setHasDraft(false);
        }}
      />
    </FooterContainer>
  );
};

CommunicationInboxProjectConversationFooter.query = gql`
  ${CommunicationInboxFooterSendText.fragment}
  ${CommunicationInboxFooterSendEmail.fragment}
  ${CommunicationInboxFooterSendCall.fragment}
  ${ProductUser.isSalesActive.fragment}

  query CommunicationInboxProjectConversationFooter_User (
    $customerUuid: String!,
    $projectUuid: String!,
    $categories: [String],
  ) {
    ${gql.query}

    user(uuid: $customerUuid) {
      id
      phoneNumber
      email
      ...CommunicationInboxFooterSendText_User
      ...CommunicationInboxFooterSendEmail_User
      ...CommunicationInboxFooterSendCall_User
    }
    viewer {
      id
      productUsers {
        id
        ...ProductUser_isSalesActive
      }
      ...CommunicationInboxFooterSendText_Viewer
      ...CommunicationInboxFooterSendCall_Viewer
    }
    project(uuid: $projectUuid) {
      id
      uuid
      organization {
            id
            textMessageTemplatesByCategory(categories: $categories) {
              id
              uuid
              ...CommunicationInboxFooterSendText_TextMessageTemplate
            }
      }
      ...CommunicationInboxFooterSendCall_Project
      ...CommunicationInboxFooterSendText_Project
      ...CommunicationInboxFooterSendEmail_Project
    }
  }
`;

export default CommunicationInboxProjectConversationFooter;
